import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { FacetFilter } from "../../../../dto/FacetFilter";
import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import { SearchSortType } from "../../account/lists/SavedListSortControl";
import { ResourceView } from "../../../../dto/ResourceView";
import { SearchResultView } from "../SearchResultView";
import { ReadingLevelFilters } from "@tlc/ls2pac/components/BooleanSearchForm";
import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { useAccount } from "../../../Account";
import {
  isAdvancedSearchTerm,
  isReadingLevelSearch,
} from "../advanced/AdvancedSearchTerm";
import { SearchResultsViewType } from "@tlc/ls2pac/components/LocationConfiguration";

export interface SearchTermAdapter<T> {
  /**
   * Parse term from the url params
   * @param sp
   */
  getTerm(sp: URLSearchParams): T;
  /**
   * Conversion of term to string for SearchParametersDWR.searchTerm
   * @param term
   */
  toApiString(term: T): string;
  /**
   * Write term to the the url params
   * @param sp
   * @param term
   */
  setTerm(sp: URLSearchParams, term: T): URLSearchParams;
}

export const basicSearchTermAdapter: SearchTermAdapter<string> = {
  getTerm: function (sp: URLSearchParams): string {
    return sp.get("term") || "";
  },
  toApiString: function (term: string): string {
    return term || "";
  },
  setTerm: function (sp: URLSearchParams, term: string): URLSearchParams {
    sp.set("term", term);
    return sp;
  },
};

export interface LSSearchParams {
  rawTerm: any;
  term: string;
  page: number;
  pageSize: number;
  view: ViewType;
  sort: SearchSortType;
  audience: AudienceType;
  facetFilters: FacetFilter[];
  facetFiltersKey: string;
  branchFilters: string[];
  branchFiltersKey: string;
  // TODO having readingLevelFiltersKey here defeats the purpose of the adapter, try again
  readingLevelFiltersKey: string;
  hasFilters: boolean;
}

interface LSSearchContextType {
  setAudienceFilter: (audience: AudienceType) => void;
  clearAudienceFilter: () => void;
  addFacetFilter: (filter: FacetFilter) => void;
  clearFacetFilter: (filter: FacetFilter) => void;
  clearAllFilters: () => void;
  setSort: (sort: SearchSortType) => void;
  /**
   * @param page 1 based page index
   */
  setPage: (page: number) => void;
  setView: (view: ViewType) => void;
  searchParams: Readonly<LSSearchParams>;
  totalHits: Readonly<number | null>;
  resources: Readonly<ResourceView[] | null>;
  resourcesKey: Readonly<string | null>;
  facetResults: Readonly<FacetResultDwr[] | null>;
  loadingResults: Readonly<boolean>;
  loadingFacets: Readonly<boolean>;
  searchError: Readonly<boolean>;
}

export enum AudienceType {
  AllAges = "",
  AdultGeneral = "Adult / General",
  Juvenile = "Juvenile",
}

export function isAudienceType(param: string): param is AudienceType {
  return Object.values(AudienceType).includes(param as AudienceType);
}

function isSearchSortType(param: string): param is SearchSortType {
  return Object.values(SearchSortType).includes(param as SearchSortType);
}

function getFacetFilterKey(filters: FacetFilter[]): string {
  return filters
    .map(({ facetName, facetValue }) => `${facetName}:${facetValue}`)
    .sort()
    .join("|");
}

function getReadingLevelFiltersKey(rawTerm: unknown) {
  if (isReadingLevelSearch(rawTerm)) {
    return Array.from(Object.entries(rawTerm.readingLevelFilters))
      .filter(([_, value]) => !!value)
      .map(([key, value]) => `${key}:${value}`)
      .join("|");
  }
  return "";
}

function getBranchFilterKey(branchFilters: string[]): string {
  return [...branchFilters].sort().join("|");
}

function getSearchResultResourceKey(resources: ResourceView[]) {
  if (!resources) {
    return null;
  }
  return resources
    .map((r) => r.id)
    .sort()
    .toString();
}

export enum ViewType {
  list = "list",
  grid = "grid",
}

function isViewType(param: string): param is ViewType {
  return Object.values(ViewType).includes(param as ViewType);
}

export function getBranchFilters(searchParams: URLSearchParams): string[] {
  const branchFiltersParam = searchParams.get("branchFilters");

  if (!branchFiltersParam) {
    return null;
  }

  try {
    const branchFilters = JSON.parse(branchFiltersParam);
    return Array.isArray(branchFilters) ? branchFilters : [];
  } catch (e: unknown) {
    console.error("Error parsing branchFilters", e);
    return null;
  }
}

export function getFacetFilters(searchParams: URLSearchParams): FacetFilter[] {
  try {
    const facetFilterParams = searchParams.get("facetFilters");
    if (!facetFilterParams) {
      return [];
    }
    const facetFilters: FacetFilter[] = JSON.parse(facetFilterParams);
    if (Array.isArray(facetFilters) && facetFilters.every(isFacetFilter)) {
      // return facetFilters;
      // TODO once backend bug is fixed remove stopgap for facetDisplay
      return facetFilters.map(({ facetDisplay, facetName, facetValue }) => {
        return {
          facetDisplay: facetDisplay || facetValue,
          facetName,
          facetValue,
        };
      });
    }
    console.warn("Ignoring invalid facetFilters param");
    return [];
  } catch (e: unknown) {
    console.error("Error parsing facetFilters param", e);
    return [];
  }
}

export function getAudienceFilter(searchParams: URLSearchParams): AudienceType {
  const audienceParam = searchParams.get("audience");
  const audience =
    audienceParam && isAudienceType(audienceParam)
      ? audienceParam
      : AudienceType.AllAges;
  return audience;
}

export interface FacetValue {
  value: string;
  displayValue: string;
  count: number;
}

export interface FacetResultDwr {
  facetName: string;
  facetDisplayName: string;
  totalValueCount: number;
  facetValues: FacetValue[];
  moreResults: boolean;
}

function isFacetFilter(item: object): item is FacetFilter[] {
  if (typeof item !== "object" || item === null) return false;

  const f = item as Partial<FacetFilter>;
  const hasValidFacetName = typeof f.facetName === "string";
  const hasValidFacetValue = typeof f.facetValue === "string";
  // TODO once backend bug for saved searches is fixed restore hasValidFacetDisplay check
  // const hasValidFacetDisplay = typeof f.facetDisplay === "string";

  // return hasValidFacetName && hasValidFacetValue && hasValidFacetDisplay;
  return hasValidFacetName && hasValidFacetValue;
}

const LSSearchContext = createContext<LSSearchContextType | undefined>(
  undefined
);

export const useLSSearch = () => {
  const ctx = useContext(LSSearchContext);
  if (!ctx) throw new Error("useLSSearch must be used within LSSearchProvider");
  return ctx;
};

const PAGE_SIZE = 24;

export interface SearchParametersDwr {
  searchTerm: string;
  startIndex?: number;
  hitsPerPage: number;
  facetFilters?: FacetFilter[];
  branchFilters?: string[];
  sortCriteria: string;
  targetAudience?: string;
  addToHistory?: boolean;
  searchedFromKids?: boolean;
  /**
   * @deprecated use sortCriteria instead
   */
  sortType?: string;
  dbCodes?: string[];
  dbCodeCounts?: Map<string, number>;
  projectedFields?: string[];
  audienceCharacteristicsFilters?: string[];
  readingLevelFilters?: ReadingLevelFilters;
  worldBookDatabaseList?: string[];
}

export interface SearchWithParametersResponse extends SearchResultView {
  pageSize: number;
  pages: number;
}

/**
 *
 * @param params LSSearchParams
 * @param configLimitedBranchIds fallback if branchFilters is empty
 * @returns
 */
export async function searchWithParameters(
  {
    term,
    page,
    sort,
    audience,
    facetFilters,
    branchFilters,
    rawTerm,
    pageSize,
  }: LSSearchParams,
  configLimitedBranchIds?: string[]
) {
  const searchBranchIds = !branchFilters.length
    ? configLimitedBranchIds
    : branchFilters;
  const startIndex = page <= 1 ? 0 : (page - 1) * pageSize;

  const addToHistory =
    !isReadingLevelSearch(rawTerm) && !isAdvancedSearchTerm(rawTerm);

  const payload: SearchParametersDwr = {
    searchTerm: term,
    hitsPerPage: pageSize,
    startIndex: startIndex,
    sortCriteria: sort,
    facetFilters,
    branchFilters: searchBranchIds,
    audienceCharacteristicsFilters:
      audience === AudienceType.AllAges ? [] : [audience],
    addToHistory,
  };
  if (isReadingLevelSearch(rawTerm)) {
    payload.readingLevelFilters = rawTerm.readingLevelFilters;
  }
  try {
    const { data } = await axios.post<SearchResultView>(`/search`, payload);
    const response: SearchWithParametersResponse = {
      pages: Math.ceil(data.totalHits / pageSize),
      pageSize: pageSize,
      ...data,
    };
    return response;
  } catch (e: unknown) {
    if (typeof e === "string") {
      throw new Error(e);
    } else {
      console.error(e);
      throw new Error("Unexpected error calling /search");
    }
  }
}

export async function getFacetResults() {
  const { data } = await axios.get<FacetResultDwr[]>(`/facets/getFacetResults`);
  return data;
}

export const AudienceHolder = (() => {
  let audience: AudienceType = AudienceType.AllAges;
  return {
    getAudience: () => {
      return audience;
    },
    setAudience: (a: AudienceType) => {
      audience = a;
    },
  };
})();

export const ResultsViewHolder = (() => {
  let resultsView: SearchResultsViewType;
  return {
    getResultsView: () => {
      return resultsView;
    },
    setResultsView: (rv: SearchResultsViewType) => {
      resultsView = rv;
    },
  };
})();

export function getSearchState(
  searchParams: URLSearchParams,
  termAdapter = basicSearchTermAdapter
) {
  const rawTerm = termAdapter.getTerm(searchParams);
  const term = termAdapter.toApiString(rawTerm);

  const facetFilters = getFacetFilters(searchParams);

  const branchFilters = getBranchFilters(searchParams) || [];

  const audience = getAudienceFilter(searchParams);

  const sortParam = searchParams.get("sort");
  const sort =
    sortParam && isSearchSortType(sortParam)
      ? sortParam
      : SearchSortType.Relevancy;

  const viewParam = searchParams.get("view");
  const view = viewParam && isViewType(viewParam) ? viewParam : ViewType.list;

  const pageSize = PAGE_SIZE;

  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const page = pageParam < 1 ? 1 : pageParam;

  const hasFilters =
    audience !== AudienceType.AllAges ||
    branchFilters.length > 0 ||
    facetFilters.length > 0;

  const params: LSSearchParams = {
    rawTerm,
    term,
    page,
    sort,
    view,
    pageSize,
    audience,
    facetFilters,
    branchFilters,
    hasFilters,
    facetFiltersKey: getFacetFilterKey(facetFilters),
    branchFiltersKey: getBranchFilterKey(branchFilters),
    readingLevelFiltersKey: getReadingLevelFiltersKey(rawTerm),
  };

  return params;
}

export const LSSearchProvider: React.FC<{
  children: React.ReactNode;
  termAdapter?: SearchTermAdapter<any>;
}> = ({ children, termAdapter = basicSearchTermAdapter }) => {
  const { isAuthenticated } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateSelectedBranches, configLimitedBranchIds } = useBranchLimits();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [resources, setResources] = useState<ResourceView[] | null>(null);
  const [loadingFacets, setLoadingFacets] = useState<boolean>(false);
  const [facetResults, setFacetResults] = useState<FacetResultDwr[] | null>(
    null
  );

  const resourcesKey = useMemo(
    () => getSearchResultResourceKey(resources),
    [resources]
  );

  const state = useMemo(() => {
    console.log("LSSearch params changed");
    return getSearchState(searchParams, termAdapter);
  }, [searchParams]);

  useEffect(() => {
    updateSelectedBranches(state.branchFilters);
  }, [state.branchFiltersKey]);

  const lastParamsRef = useRef<
    Pick<
      LSSearchParams,
      | "term"
      | "audience"
      | "branchFiltersKey"
      | "facetFiltersKey"
      | "readingLevelFiltersKey"
    >
  >({
    term: null,
    audience: null,
    branchFiltersKey: null,
    facetFiltersKey: null,
    readingLevelFiltersKey: null,
  });
  const shouldFetchFacetsRef = useRef<boolean>(false);

  useEffect(() => {
    if (!state) {
      return;
    }

    setLoadingResults(true);
    const {
      term,
      audience,
      facetFiltersKey,
      branchFiltersKey,
      readingLevelFiltersKey,
    } = state;
    shouldFetchFacetsRef.current =
      lastParamsRef.current.audience !== audience ||
      lastParamsRef.current.term !== term ||
      lastParamsRef.current.branchFiltersKey !== branchFiltersKey ||
      lastParamsRef.current.facetFiltersKey !== facetFiltersKey ||
      lastParamsRef.current.readingLevelFiltersKey !== readingLevelFiltersKey;
    if (shouldFetchFacetsRef.current) {
      setLoadingFacets(true);
    }

    searchWithParameters(state, configLimitedBranchIds)
      .then((response) => {
        setTotalHits(response.totalHits);
        setResources(response.resources);
        setLoadingResults(false);
        setSearchError(false);
        if (shouldFetchFacetsRef.current) {
          lastParamsRef.current = {
            term,
            audience,
            facetFiltersKey,
            branchFiltersKey,
            readingLevelFiltersKey,
          };
          getFacetResults().then((value) => {
            setFacetResults(value);
            setLoadingFacets(false);
          });
        }
      })
      .catch(() => {
        setSearchError(true);
        setTotalHits(0);
        setResources([]);
        setLoadingResults(false);
        setLoadingFacets(false);
        setFacetResults([]);
      });
  }, [
    state.audience,
    state.branchFiltersKey,
    state.facetFiltersKey,
    state.readingLevelFiltersKey,
    state.page,
    state.pageSize,
    state.sort,
    state.term,
    configLimitedBranchIds,
  ]);

  const setAudienceFilter = (type: AudienceType): void => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      AudienceHolder.setAudience(type);
      if (!type) {
        newParams.delete("audience");
      } else {
        newParams.set("audience", type);
      }
      return newParams;
    });
  };

  const clearAudienceFilter = () => {
    setSearchParams((prev) => {
      AudienceHolder.setAudience(AudienceType.AllAges);
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      newParams.delete("audience");
      return newParams;
    });
  };

  const addFacetFilter = (facetFilter: FacetFilter): void => {
    const facetFilters = getFacetFilters(searchParams);
    const newFacetFilters = [
      facetFilter,
      ...facetFilters.filter(
        ({ facetName, facetValue }) =>
          facetValue !== facetFilter.facetValue ||
          facetName !== facetFilter.facetName
      ),
    ];
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      newParams.set("facetFilters", JSON.stringify(newFacetFilters));
      return newParams;
    });
  };

  const clearFacetFilter = ({ facetValue, facetName }: FacetFilter): void => {
    const facetFilters = getFacetFilters(searchParams);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      const newFacetFilters = facetFilters.filter(
        (ff) => facetValue !== ff.facetValue || facetName !== ff.facetName
      );
      if (newFacetFilters.length === 0) {
        newParams.delete("facetFilters");
      }
      newParams.set("facetFilters", JSON.stringify(newFacetFilters));
      return newParams;
    });
  };

  const clearAllFilters = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      newParams.delete("facetFilters");
      newParams.delete("audience");
      AudienceHolder.setAudience(AudienceType.AllAges);
      newParams.set("branchFilters", JSON.stringify([])); // need to explicitly set to [] to clear because of context fallback
      return newParams;
    });
  };
  const setPage = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (page <= 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      return newParams;
    });
  };

  const setView = useCallback(
    (view: ViewType) => {
      if (!view) return;
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("view", view);
        ResultsViewHolder.setResultsView(
          view === ViewType.grid
            ? SearchResultsViewType.gridview
            : SearchResultsViewType.listview
        );
        return newParams;
      });
    },
    [setSearchParams]
  );

  const setSort = (sort: SearchSortType) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("sort", sort);
      newParams.delete("page");
      return newParams;
    });
  };

  const contextValue: LSSearchContextType = useMemo(() => {
    return {
      setAudienceFilter,
      clearAudienceFilter,
      addFacetFilter,
      clearFacetFilter,
      clearAllFilters,
      setSort,
      setPage,
      setView,
      searchParams: state,
      totalHits,
      resources,
      resourcesKey,
      facetResults,
      loadingResults,
      loadingFacets,
      searchError,
    };
  }, [
    state,
    resourcesKey,
    totalHits,
    facetResults,
    loadingFacets,
    loadingResults,
  ]);

  return (
    <LSSearchContext.Provider value={contextValue}>
      {children}
    </LSSearchContext.Provider>
  );
};
